import { ShoppingCartWidgetUpdate, ShoppingCartWidget, WidgetBase } from './shopping-cart.model';
import { getCartCount, getPurchaseListCount } from './shopping-cart.service';

const ATTRIBUTE_SHOPPING_CART_WIDGET_CART_AMOUNT = 'data-klt-c-shopping-cart-widget-cart-amount';
const ATTRIBUTE_SHOPPING_CART_WIDGET_PURCHASE_LIST_AMOUNT = 'data-klt-c-shopping-cart-widget-purchase-list-amount';
const _widgets: ShoppingCartWidget = { cart: { amount: 0 }, purchaseList: { amount: 0 } };

// Replace the counter in the widgets e.g. "Warenklorb (0)" to "Warenkorb (1)"
const reg = new RegExp(/\(([^\)]+)\)/g);
const updateTextCounter = (text: string, quantity: number | string) => text.replace(reg, quantity.toString());

const getCartWidgetCartElements = () =>
    _widgets.cart.elements || document.querySelectorAll(`[${ATTRIBUTE_SHOPPING_CART_WIDGET_CART_AMOUNT}]`);

const getCartWidgetPurchaseListElements = () =>
    _widgets.purchaseList.elements ||
    document.querySelectorAll(`[${ATTRIBUTE_SHOPPING_CART_WIDGET_PURCHASE_LIST_AMOUNT}]`);

/**
 * Initalizes all cart widgets by checking for properties given on the component
 * and make an API request if apiEndpoint property is set.
 */
function initCartWidget() {
    try {
        initCartItems();
        initPurchaseListItems();
    } catch (e) {
        console.error(`Couldn't initialize shopping cart widget module.`, e);
    }
}

async function initPurchaseListItems() {
    const purchaseListElements = getCartWidgetPurchaseListElements();
    if (purchaseListElements && purchaseListElements.length > 0) {
        // In theory there could be more than one cart widget, that's why we let the handler
        // update every widget. Can be performance optimized in the future by
        // checking if the object/options on the component are identical and e.g. only request
        // once from the same API. But as the widget is only used once yet, this doesn't matter.
        for (const element of Array.from(purchaseListElements)) {
            // There can be data on the value of ATTRIBUTE_SHOPPING_CART_WIDGET_CART_AMOUNT, e.g. in this case
            // the API endpoint to get the count of the cart.
            const propsRaw = element.getAttribute(ATTRIBUTE_SHOPPING_CART_WIDGET_PURCHASE_LIST_AMOUNT);
            if (propsRaw) {
                const props = JSON.parse(propsRaw) as {
                    apiEndpoint: string;
                };
                if (props.apiEndpoint) {
                    try {
                        const response = await getPurchaseListCount(props.apiEndpoint);
                        if (response.shopping_list && response.shopping_list.list) {
                            // Handle cart widget update
                            const purchaseList = response.shopping_list.list.item_count;
                            updateCartWidget({ purchaseList });
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }
            }
        }
    }
}

async function initCartItems() {
    const cartElements = getCartWidgetCartElements();
    if (cartElements && cartElements.length > 0) {
        // In theory there could be more than one cart widget, that's why we let the handler
        // update every widget. Can be performance optimized in the future by
        // checking if the object/options on the component are identical and e.g. only request
        // once from the same API. But as the widget is only used once yet, this doesn't matter.
        for (const element of Array.from(cartElements)) {
            // There can be data on the value of ATTRIBUTE_SHOPPING_CART_WIDGET_CART_AMOUNT, e.g. in this case
            // the API endpoint to get the count of the cart.
            const propsRaw = element.getAttribute(ATTRIBUTE_SHOPPING_CART_WIDGET_CART_AMOUNT);
            if (propsRaw) {
                const props = JSON.parse(propsRaw) as { apiEndpoint: string };
                if (props.apiEndpoint) {
                    try {
                        const response = await getCartCount(props.apiEndpoint);
                        if (response.basket && response.basket.cart) {
                            // Handle cart widget update
                            const cart = response.basket.cart.item_count;
                            updateCartWidget({ cart });
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }
            }
        }
    }
}

/**
 * Update cart widget DOM with the given parameters
 */
export async function updateCartWidget({ cart, purchaseList }: ShoppingCartWidgetUpdate) {
    if (cart) {
        const elements = getCartWidgetCartElements();
        _widgets.cart = await updateWidgetPart({ ..._widgets.cart, elements }, cart);
    }

    if (purchaseList) {
        const elements = getCartWidgetPurchaseListElements();
        _widgets.purchaseList = await updateWidgetPart({ ..._widgets.purchaseList, elements }, purchaseList);
    }
}

/**
 * Update the cart counter of the given list of widgets
 */
async function updateWidgetPart(widget: WidgetBase, quantity: number) {
    if (!widget) {
        console.error('klt/shopping cart widget: No widget found');
    }

    if (!quantity) {
        console.error('klt/shopping cart widget: Quantity missing');
    }

    const cartCounter = `(${quantity})`;

    widget.amount = quantity;
    widget.elements.forEach(element => {
        const currentWidgetText = element.innerHTML;

        // Test if "Label (<count>) exists, and depending on that, replace the counter, remove it or add it if missing.
        if (reg.test(currentWidgetText)) {
            element.innerHTML =
                quantity > 0
                    ? updateTextCounter(currentWidgetText, cartCounter)
                    : updateTextCounter(currentWidgetText, '');
        } else {
            const labelElement = element.querySelector('[data-c-icon-label]');

            if (labelElement) {
                labelElement.innerHTML =
                    quantity > 0 ? `${labelElement.innerHTML} ${cartCounter}` : labelElement.innerHTML;
            }
        }
    });

    return widget;
}

export default initCartWidget;
